import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Bell, ChevronRight, Loader2, Pencil } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useEnterpriseOptional } from "@/contexts/EnterpriseContext";
import { DeleteAccountDialog } from "@/components/shared/DeleteAccountDialog";
import { CoverageAreasCard } from "@/components/features/profile/coverage-areas-card";
import { MyFiscalDeclarationCard } from "@/components/shared/MyFiscalDeclarationCard";
import { MyTwoFactorCard } from "@/components/shared/MyTwoFactorCard";
import { useMyProfileAvatarObjectUrl } from "@/hooks/useMyProfileAvatarObjectUrl";
import { UserAvatar } from "@/components/ui/user-avatar";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import { setPassword } from "@/services/authService";
import { practisesAnyProfession } from "@/lib/is-professional";

const MIN_PASSWORD_LENGTH = 8;

function getDisplayName(
  firstName: string | null,
  lastName: string | null,
  displayName: string | null,
  email: string
): string {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (displayName?.trim()) return displayName;
  return email;
}

export function ProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const enterpriseCtx = useEnterpriseOptional();
  const isEnterprise = enterpriseCtx?.enterprise != null;

  const canDeleteAccount =
    user != null &&
    !isEnterprise &&
    user.platformRole !== "root" &&
    user.platformRole !== "root_admin";

  /**
   * SSO-only users (Google / Apple) have no passwordHash — the "change password"
   * form would let them enter a current password they don't have. Hide the
   * section entirely until we offer a dedicated "set a password" affordance.
   */
  const canChangePassword = user?.hasPassword !== false;

  const isProfessional = practisesAnyProfession(user);

  const displayName = getDisplayName(
    user?.firstName ?? null,
    user?.lastName ?? null,
    user?.displayName ?? null,
    user?.email ?? ""
  );

  const profileAvatarSrc = useMyProfileAvatarObjectUrl(user?.profileImage);

  const handleSubmitPassword = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        toast.error(t("settings.password.error") + " " + t("settings.password.minLength"));
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error(t("settings.password.error"));
        return;
      }
      setPasswordSaving(true);
      try {
        await setPassword({
          currentPassword: currentPassword || undefined,
          newPassword,
        });
        await refreshUser();
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        toast.success(t("settings.password.success"));
      } catch {
        toast.error(t("settings.password.error"));
      } finally {
        setPasswordSaving(false);
      }
    },
    [newPassword, confirmPassword, currentPassword, refreshUser, t]
  );

  if (!user) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader title={t("profile.title", "Profile")} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4 md:p-8">
      <PageHeader
        title={displayName}
        description={t("profile.description", "Your account details")}
        actions={
          <Button variant="outline" asChild>
            <Link to="/profile/edit" className="inline-flex items-center">
              <Pencil className="mr-2 h-4 w-4" />
              {t("profile.edit", "Edit")}
            </Link>
          </Button>
        }
      />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("profile.personalInfo", "Personal information")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="flex-shrink-0">
              <UserAvatar
                size="2xl"
                firstName={user.firstName}
                lastName={user.lastName}
                email={user.email}
                displayName={user.displayName ?? displayName}
                imageSrc={profileAvatarSrc}
              />
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.firstName", "First name")}
                </p>
                <p className="text-sm text-foreground">
                  {user.firstName?.trim() || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.lastName", "Last name")}
                </p>
                <p className="text-sm text-foreground">
                  {user.lastName?.trim() || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.email", "Email")}
                </p>
                <p className="text-sm text-foreground">{user.email}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.phone", "Phone")}
                </p>
                <p className="text-sm text-foreground">
                  {user.phone?.trim() ? user.phone : "—"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Professionals only. A consumer has no fiscal declaration to make —
          the server's own PUT refuses a row that is neither notary nor
          accountant, so showing the card to everyone would offer a form that
          cannot be submitted. */}
      {isProfessional ? (
        <MyFiscalDeclarationCard />
      ) : null}

      {/* Beside the fiscal declaration, and professionals only, for the same
          reason: it is a professional-only fact that the server reads on their
          behalf, and it previously lived on /profile/edit where nobody looking
          for it would think to go. */}
      {isProfessional ? <CoverageAreasCard /> : null}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4 text-muted-foreground" />
            {t("profile.notifications.title", "Notificaciones")}
          </CardTitle>
          <CardDescription>
            {t("profile.notifications.description", "Configurá qué notificaciones recibís por email y push.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <Button variant="outline" asChild>
            <Link to="/profile/notifications" className="inline-flex items-center">
              {t("profile.notifications.manage", "Administrar preferencias")}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
      {/* Professionals only, gated on the same term as the route it links to
          (ProtectedRoute allowedRoles) and as the enrollment gate: TOTP is a
          professional capability, and a consumer sent to /security/two-factor
          would be bounced by the router. Next to the password, because this is
          a security setting and that is where someone looks for it. */}
      {isProfessional ? <MyTwoFactorCard /> : null}

      {canChangePassword ? (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("settings.password.title")}
          </CardTitle>
          <CardDescription>
            {t("settings.password.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <form
            onSubmit={handleSubmitPassword}
            className="flex w-full flex-col gap-3"
          >
            <div className="w-full space-y-1.5">
              <Label
                htmlFor="profile-current-password"
                className="text-xs font-medium text-muted-foreground"
              >
                {t("settings.password.currentPassword")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.password.currentPasswordHint")}
              </p>
              <PasswordInput
                id="profile-current-password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full transition-colors duration-150"
              />
            </div>
            <div className="w-full space-y-1.5">
              <Label
                htmlFor="profile-new-password"
                className="text-xs font-medium text-muted-foreground"
              >
                {t("settings.password.newPassword")}
              </Label>
              <PasswordInput
                id="profile-new-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={MIN_PASSWORD_LENGTH}
                className="w-full transition-colors duration-150"
              />
              <PasswordStrengthIndicator
                password={newPassword}
                showRules
                rulesLabels={{
                  minLength: t("auth.register.passwordStrength.minLength"),
                  uppercase: t("auth.register.passwordStrength.uppercase"),
                  number: t("auth.register.passwordStrength.number"),
                  special: t("auth.register.passwordStrength.special"),
                }}
                strengthLabels={{
                  weak: t("auth.register.passwordStrength.weak"),
                  fair: t("auth.register.passwordStrength.fair"),
                  good: t("auth.register.passwordStrength.good"),
                  strong: t("auth.register.passwordStrength.strong"),
                }}
              />
            </div>
            <div className="w-full space-y-1.5">
              <Label
                htmlFor="profile-confirm-password"
                className="text-xs font-medium text-muted-foreground"
              >
                {t("settings.password.confirmPassword")}
              </Label>
              <PasswordInput
                id="profile-confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full transition-colors duration-150"
              />
            </div>
            <div className="flex w-full justify-end">
              <Button
                type="submit"
                disabled={passwordSaving}
                className="transition-colors duration-150"
              >
                {passwordSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("settings.password.saving")}
                  </>
                ) : (
                  t("settings.password.save")
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      ) : null}
      {canDeleteAccount ? (
        <Card className="border-destructive/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">
              {t("profile.deleteAccount.sectionTitle")}
            </CardTitle>
            <CardDescription>{t("profile.deleteAccount.description")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Button
              type="button"
              variant="destructive"
              className="transition-colors duration-150"
              onClick={() => setDeleteDialogOpen(true)}
            >
              {t("profile.deleteAccount.open")}
            </Button>
            <DeleteAccountDialog
              open={deleteDialogOpen}
              onOpenChange={setDeleteDialogOpen}
              accountEmail={user.email}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
